import type {
  HealthResponse,
  GatewayPosition,
  PortfolioMetrics,
  GatewayBarData,
  ArsenalCombo,
  AccountBalance,
  ComboStatus,
  SignalData,
  ComboGenome,
  AlphaEvalReport,
  ExternalFeed,
  RegimeState,
  RegimeRecord,
  EquityRecord,
  JobStatus,
  DispatchRequest,
  DispatchResponse,
  OrderData,
  OrderRequest,
  OrderResponse,
  ExchangeReferenceDriftTradingBlockStatus,
  InstrumentInfo,
  HyperliquidAddressSync,
  ReadOnlyConnectorCatalog,
  ReadOnlyExchangeSyncSummary,
  ReadOnlyExchangeVenue,
  ReadOnlyIngestionResponse,
  TradingDnaReportRequest,
  TradingDnaReportResponse,
  VarConeResponse,
} from '../types/gateway';

// Dev (Vite proxy on localhost:3000): BASE_URL='' · proxy forwards /api/* + /ws to api.trade-matrix-labs.tech
// Prod (CF Pages): VITE_API_BASE_URL='https://api.trade-matrix-labs.tech' (set in CF Pages env vars)
// Empty default preserves dev behavior · prod build bakes the env var at compile time
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export class GatewayError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`Gateway ${status}: ${body}`);
    this.name = 'GatewayError';
  }
}

// DDD #107 S3 T3.1 — Cloudflare Access auth helpers.
// CF Access primary identity endpoint is the sole auth path for beta.
// Fallback removed: /api/auth/me is not registered in tm-gateway (Codex P2 finding · no route echoes CF email).
export const CF_IDENTITY_URL = '/cdn-cgi/access/get-identity';
export const AUTH_FETCH_TIMEOUT_MS = 5_000;

/**
 * Composite CF Access auth-redirect detection per DDD #107 S3 spec §3.
 * CF Access never returns 302 to fetch AJAX, so we infer "needs login"
 * from multiple response signatures.
 */
export function isCfAuthRedirect(res: Response): boolean {
  if (res.type === 'opaqueredirect') return true;
  if (res.redirected && res.url.includes('/cdn-cgi/access/login')) return true;
  if (res.status === 401) return true;
  if (res.status === 403 && res.headers.get('cf-mitigated') === 'access') return true;
  if (res.status === 401 || res.status === 403) {
    const ct = res.headers.get('content-type') ?? '';
    if (!ct.includes('application/json')) return true;
  }
  return false;
}

export function fetchCfIdentity(signal: AbortSignal): Promise<Response> {
  return fetch(CF_IDENTITY_URL, {
    credentials: 'include',
    redirect: 'manual',
    signal,
  });
}

function apiKey(): string | undefined {
  return (import.meta.env.VITE_GATEWAY_API_KEY as string | undefined) || undefined;
}

async function fetchJson<T>(path: string): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const key = apiKey();
  const res = key
    ? await fetch(url, { headers: { 'X-API-Key': key } })
    : await fetch(url);
  if (!res.ok) {
    throw new GatewayError(res.status, await res.text());
  }
  return res.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const key = apiKey();
  if (key) headers['X-API-Key'] = key;
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new GatewayError(res.status, await res.text());
  }
  return res.json() as Promise<T>;
}

async function deleteVoid(path: string): Promise<void> {
  const init: RequestInit = { method: 'DELETE' };
  const key = apiKey();
  if (key) init.headers = { 'X-API-Key': key };
  const res = await fetch(`${BASE_URL}${path}`, init);
  if (!res.ok) {
    throw new GatewayError(res.status, await res.text());
  }
}

export const gatewayApi = {
  health: (): Promise<HealthResponse> =>
    fetchJson<HealthResponse>('/health'),

  positions: (): Promise<GatewayPosition[]> =>
    fetchJson<GatewayPosition[]>('/api/positions'),

  metrics: (): Promise<PortfolioMetrics> =>
    fetchJson<PortfolioMetrics>('/api/metrics'),

  bars: (symbol?: string, timeframe?: string, limit?: number): Promise<GatewayBarData[]> => {
    const params = new URLSearchParams();
    if (symbol) params.set('symbol', symbol);
    if (timeframe) params.set('timeframe', timeframe);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return fetchJson<GatewayBarData[]>(`/api/bars${qs ? `?${qs}` : ''}`);
  },

  arsenal: (): Promise<ArsenalCombo[]> =>
    fetchJson<ArsenalCombo[]>('/api/arsenal'),

  account: (): Promise<AccountBalance> =>
    fetchJson<AccountBalance>('/api/account'),

  comboStatus: (): Promise<ComboStatus[]> =>
    fetchJson<ComboStatus[]>('/api/combo-status'),

  signals: (): Promise<SignalData[]> =>
    fetchJson<SignalData[]>('/api/signals'),

  genome: (): Promise<ComboGenome[]> =>
    fetchJson<ComboGenome[]>('/api/genome'),

  alphaEval: (): Promise<AlphaEvalReport[]> =>
    fetchJson<AlphaEvalReport[]>('/api/alpha-eval'),

  external: (source?: string): Promise<ExternalFeed[]> =>
    fetchJson<ExternalFeed[]>(`/api/external${source ? `?source=${source}` : ''}`),

  riskMetrics: (): Promise<import('../types/gateway').RiskMetrics> =>
    fetchJson('/api/risk-metrics'),

  portfolioData: (): Promise<import('../types/gateway').PortfolioData> =>
    fetchJson('/api/portfolio-data'),

  regime: (): Promise<RegimeState> =>
    fetchJson<RegimeState>('/api/regime'),

  regimeHistory: (fromMs: number, toMs: number): Promise<RegimeRecord[]> => {
    const params = new URLSearchParams({
      from: String(fromMs),
      to: String(toMs),
    });
    return fetchJson<RegimeRecord[]>(`/api/regime/history?${params.toString()}`);
  },

  portfolioEquity: (fromMs: number, toMs: number, symbol?: string): Promise<EquityRecord[]> => {
    const params = new URLSearchParams({
      from: String(fromMs),
      to: String(toMs),
    });
    if (symbol) params.set('symbol', symbol);
    return fetchJson<EquityRecord[]>(`/api/portfolio/equity?${params.toString()}`);
  },

  jobs: (): Promise<JobStatus[]> =>
    fetchJson<JobStatus[]>('/api/jobs'),

  dispatchRdLoop: (req: DispatchRequest): Promise<DispatchResponse> =>
    postJson<DispatchResponse>('/api/rdloop/dispatch', req),

  submitOrder: (req: OrderRequest): Promise<OrderResponse> =>
    postJson<OrderResponse>('/api/orders', req),

  listOrders: (): Promise<OrderData[]> =>
    fetchJson<OrderData[]>('/api/orders'),

  exchangeReferenceDriftTradingBlockStatus: (): Promise<ExchangeReferenceDriftTradingBlockStatus> =>
    fetchJson<ExchangeReferenceDriftTradingBlockStatus>(
      '/api/trading-dna/market-data/exchange-reference-drift-trading-block-status',
    ),

  instruments: (): Promise<InstrumentInfo[]> =>
    fetchJson<InstrumentInfo[]>('/api/instruments'),

  hyperliquidAddressSync: (address: string, lookbackDays = 7): Promise<HyperliquidAddressSync> => {
    const params = new URLSearchParams({
      address,
      lookback_days: String(lookbackDays),
    });
    return fetchJson<HyperliquidAddressSync>(`/api/trading-dna/hyperliquid/address-sync?${params.toString()}`);
  },

  bybitReadOnlySync: (lookbackDays = 7): Promise<ReadOnlyExchangeSyncSummary> => {
    const params = new URLSearchParams({
      lookback_days: String(lookbackDays),
    });
    return fetchJson<ReadOnlyExchangeSyncSummary>(`/api/trading-dna/bybit/read-only-sync?${params.toString()}`);
  },

  readOnlyConnectorCatalog: (): Promise<ReadOnlyConnectorCatalog> =>
    fetchJson<ReadOnlyConnectorCatalog>('/api/trading-dna/read-only-connectors'),

  readOnlyIngestion: (params: {
    venues?: ReadOnlyExchangeVenue[];
    hyperliquidAddress?: string;
    lookbackDays?: number;
  } = {}): Promise<ReadOnlyIngestionResponse> => {
    const query = new URLSearchParams();
    if (params.venues?.length) query.set('venues', params.venues.join(','));
    if (params.hyperliquidAddress) query.set('hyperliquid_address', params.hyperliquidAddress);
    if (params.lookbackDays !== undefined) query.set('lookback_days', String(params.lookbackDays));
    const qs = query.toString();
    return fetchJson<ReadOnlyIngestionResponse>(`/api/trading-dna/read-only-ingestion${qs ? `?${qs}` : ''}`);
  },

  createTradingDnaReport: (request: TradingDnaReportRequest): Promise<TradingDnaReportResponse> =>
    postJson<TradingDnaReportResponse>('/api/trading-dna/report', request),

  varcone: (symbol: string): Promise<VarConeResponse> =>
    fetchJson<VarConeResponse>(`/api/varcone?symbol=${encodeURIComponent(symbol)}`),

  cancelOrder: (id: string): Promise<void> =>
    deleteVoid(`/api/orders/${encodeURIComponent(id)}`),
};
